import { useProductAnalytics } from '@/src/api/data/analytics'
import { type LocationsContextValue, useLocationsContext } from '@/src/contexts/LocationsContext'
import { useSessionStore } from '@/src/hooks/use_session_store'
import { ALL_REGIONS_LABEL } from '@/src/utils/constants'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ILocationHierarchyOption, ILocationSelectMenuButtonLabels } from '..'
import { matchesSearchQuery } from '../SearchBox/search.utils'

const HIERARCHY_TRANSLATION_KEYS = {
  allLocations: 'common.all_locations',
  allBuildings: 'common.all_buildings',
} as const

export const getTranslatedHierarchyLabel = (item: { id: number | string; name?: string } | undefined, t: (key: string) => string) => {
  if (!item) return ''

  const translationKey = HIERARCHY_TRANSLATION_KEYS[item.id as keyof typeof HIERARCHY_TRANSLATION_KEYS]
  if (translationKey) {
    return t(translationKey)
  }
  return item.name ?? ''
}

export const constructOptions = (
  data: { id: number | string; name: string }[],
  searchTerm: string,
  priorityId: string,
  t: (key: string) => string
) =>
  data
    ?.map((item, index) => ({ value: index, label: getTranslatedHierarchyLabel(item, t), id: item.id, original: item }))
    .filter(i => matchesSparseSearch(searchTerm, i.label))
    .sort((a, b) => {
      const aIsPriority = priorityId === a.id
      const bIsPriority = priorityId === b.id

      if (aIsPriority && !bIsPriority) return -1
      if (!aIsPriority && bIsPriority) return 1

      return a.label.localeCompare(b.label.trim()) // Regular alphabetical sort
    })

const SEARCH_OPTION_DUPLICATION_THRESHOLD = 8

export const combineSearchOptionSections = <T extends { id: number | string }>(
  preferredOptions: T[],
  globalOptions: T[]
): (T & { showDividerBefore?: boolean })[] => {
  const preferredIds = new Set(preferredOptions.map(option => option.id))
  const secondaryOptions =
    globalOptions.length > SEARCH_OPTION_DUPLICATION_THRESHOLD
      ? globalOptions
      : globalOptions.filter(option => !preferredIds.has(option.id))

  return [
    ...preferredOptions,
    ...secondaryOptions.map((option, index) => ({
      ...option,
      showDividerBefore: preferredOptions.length > 0 && index === 0,
    })),
  ]
}

export const matchesSparseSearch = (term: string, stringToSearch: string) => matchesSearchQuery(term, stringToSearch)

export const shouldSetLocationLabel = (regions, locations, buildings, locationName, isAllLocationSelected: boolean) =>
  (regions.length > 1 || locations?.length >= 2 || buildings?.length > 2) && locationName && !isAllLocationSelected

const getLocationLabel = (locations, setLocationLabel, location) => {
  if (locations?.length === 2) return locations[1]?.name
  return setLocationLabel ? location?.name : ''
}

const shouldSetBuildingLabel = (shouldSetLocationLabel, buildings, isAllBuildingSelected: boolean) =>
  shouldSetLocationLabel && buildings.length > 2 && !isAllBuildingSelected

export const getVisualControlProps = (locCtx: LocationsContextValue, hoveredRegionIndex, hoveredLocationIndex) => {
  const totalLocations = locCtx.allLocations?.length ?? 0

  const hoveredRegion = locCtx.regions?.[hoveredRegionIndex]
  const hoveredLocation = hoveredRegion?.locations?.[hoveredLocationIndex]

  const totalBuildingsInList = hoveredLocation?.buildings?.length ?? 0

  const disableSelect = (totalLocations === 2 && totalBuildingsInList === 2) || locCtx.isLoading || !locCtx.isInitialized
  const showLocationList = true
  const showBuildingList = hoveredLocationIndex > 0 && totalBuildingsInList > 2

  const showAllLocationsButton = locCtx.userCanSeeAllLocations
  return { disableSelect, showLocationList, showBuildingList, showAllLocationsButton }
}

export const useLocationSelect = () => {
  const companyName = useSessionStore().user?.companyName
  const locCtx = useLocationsContext()
  const { reportEvent } = useProductAnalytics()
  const { t, i18n } = useTranslation()

  const [hoveredRegionIndex, setHoveredRegionIndex] = useState<number>(0)
  const [hoveredLocationIndex, setHoveredLocationIndex] = useState<number>(0)
  const [hoveredBuildingIndex, setHoveredBuildingIndex] = useState<number>(0)

  const [menuButtonLabels, setMenuButtonLabels] = useState<ILocationSelectMenuButtonLabels>({
    region: companyName ?? ALL_REGIONS_LABEL,
    location: '',
    building: '',
  })
  const [activeSearchBoxId, setActiveSearchBoxId] = useState<number>(-1)
  const [regionSearchTerm, setRegionSearchTerm] = useState('')
  const [locationSearchTerm, setLocationSearchTerm] = useState('')
  const [buildingSearchTerm, setBuildingSearchTerm] = useState('')

  // Keep search sections stable while hovering global results. Hover still drives the
  // dependent columns, but it should not move the result currently under the pointer.
  const [locationSearchRegionIndex, setLocationSearchRegionIndex] = useState<number>()
  const [buildingSearchHierarchy, setBuildingSearchHierarchy] = useState<{ regionIndex: number; locationIndex: number }>()

  const visualControlProps = getVisualControlProps(locCtx, hoveredRegionIndex, hoveredLocationIndex)
  const { disableSelect, showLocationList, showAllLocationsButton } = visualControlProps
  const showBuildingList = visualControlProps.showBuildingList || buildingSearchTerm.trim().length > 0

  // Get region, locations, and buildings sources to construct select options
  const regions = locCtx.regions ?? []

  const preferredLocationRegionIndex = locationSearchRegionIndex ?? hoveredRegionIndex
  const preferredLocationsOfRegion = locCtx.regions[preferredLocationRegionIndex]?.locations ?? []
  const preferredBuildingHierarchy = buildingSearchHierarchy ?? {
    regionIndex: hoveredRegionIndex,
    locationIndex: hoveredLocationIndex,
  }
  const preferredBuildingsOfLocation =
    locCtx.regions[preferredBuildingHierarchy.regionIndex]?.locations[preferredBuildingHierarchy.locationIndex]?.buildings ?? []

  // Construct options
  const regionOptions = useMemo<ILocationHierarchyOption[]>(() => {
    const defaultLocationIndex = showAllLocationsButton ? 0 : 1

    return constructOptions(regions, regionSearchTerm, 'allGroups', t).map(option => ({
      ...option,
      menuKey: `region:${option.value}`,
      hierarchy: {
        regionIndex: option.value,
        locationIndex: defaultLocationIndex,
        buildingIndex: 0,
      },
    }))
  }, [regionSearchTerm, locCtx.regions, showAllLocationsButton, t, i18n.language])

  const locationOptions = useMemo<ILocationHierarchyOption[]>(() => {
    const isSearchActive = locationSearchTerm.trim().length > 0
    const preferredOptions = constructOptions(preferredLocationsOfRegion, locationSearchTerm, 'allLocations', t)
      .filter(option => showAllLocationsButton || option.original.id !== 'allLocations')
      .map(option => ({
        ...option,
        menuKey: `location:preferred:${preferredLocationRegionIndex}:${option.value}`,
        hierarchy: {
          regionIndex: preferredLocationRegionIndex,
          locationIndex: option.value,
          buildingIndex: 0,
        },
      }))
    if (!isSearchActive) return preferredOptions

    const globalOptions = constructOptions(locCtx.allLocations, locationSearchTerm, 'allLocations', t)
      .filter(option => option.original.id !== 'allLocations')
      .map(option => ({
        ...option,
        menuKey: `location:global:0:${option.value}`,
        hierarchy: {
          regionIndex: 0,
          locationIndex: option.value,
          buildingIndex: 0,
        },
      }))

    return combineSearchOptionSections(preferredOptions, globalOptions)
  }, [
    preferredLocationsOfRegion,
    preferredLocationRegionIndex,
    locationSearchTerm,
    locCtx.allLocations,
    showAllLocationsButton,
    t,
    i18n.language,
  ])

  const buildingOptions = useMemo<ILocationHierarchyOption[]>(() => {
    const isSearchActive = buildingSearchTerm.trim().length > 0
    const preferredOptions = constructOptions(preferredBuildingsOfLocation, buildingSearchTerm, 'allBuildings', t).map(option => ({
      ...option,
      menuKey: `building:preferred:${preferredBuildingHierarchy.regionIndex}:${preferredBuildingHierarchy.locationIndex}:${option.value}`,
      hierarchy: {
        ...preferredBuildingHierarchy,
        buildingIndex: option.value,
      },
    }))
    if (!isSearchActive) return preferredOptions

    const globalOptions = locCtx.allLocations
      .flatMap((location, locationIndex) => {
        if (location.id === 'allLocations') return []

        return constructOptions(location.buildings, buildingSearchTerm, 'allBuildings', t)
          .filter(option => option.original.id !== 'allBuildings')
          .map(option => ({
            ...option,
            menuKey: `building:global:0:${locationIndex}:${option.value}`,
            hierarchy: {
              regionIndex: 0,
              locationIndex,
              buildingIndex: option.value,
            },
          }))
      })
      .sort((a, b) => a.label.localeCompare(b.label.trim()))

    return combineSearchOptionSections(preferredOptions, globalOptions)
  }, [preferredBuildingsOfLocation, preferredBuildingHierarchy, buildingSearchTerm, locCtx.allLocations, t, i18n.language])

  // On context values change update selected indexes and labels
  useEffect(() => {
    if (!locCtx.isInitialized) return

    const regions = locCtx.regions
    const region = locCtx.regions?.[locCtx.selectedRegionIndex]

    const locations = region.locations
    const location = locations[locCtx.selectedLocationIndex]
    const isAllLocationSelected = location ? locCtx.isAllLocation(location) : false

    const buildings = location.buildings
    const building = buildings[locCtx.selectedBuildingIndex]
    const isAllBuildingSelected = building ? locCtx.isAllBuilding(building) : false

    setHoveredRegionIndex(locCtx.selectedRegionIndex)
    setHoveredLocationIndex(locCtx.selectedLocationIndex)
    setHoveredBuildingIndex(locCtx.selectedBuildingIndex)
    // Set labels of button
    const setLocationLabel = shouldSetLocationLabel(regions, locations, buildings, location?.name, isAllLocationSelected)

    const setBuildingLabel = shouldSetBuildingLabel(setLocationLabel, buildings, isAllBuildingSelected)
    const locationLabel = getLocationLabel(
      locations?.map(item => ({ ...item, name: getTranslatedHierarchyLabel(item, t) })),
      setLocationLabel,
      location ? { ...location, name: getTranslatedHierarchyLabel(location, t) } : location
    )
    setMenuButtonLabels({
      region: getTranslatedHierarchyLabel(region, t),
      location: locationLabel,
      building: setBuildingLabel ? getTranslatedHierarchyLabel(building, t) : '',
    })
  }, [locCtx.isInitialized, locCtx.selectedRegionIndex, locCtx.selectedLocationIndex, locCtx.selectedBuildingIndex, t, i18n.language])

  // Event handlers
  const handleRegionChange = (option: ILocationHierarchyOption) => {
    const { regionIndex, locationIndex, buildingIndex } = option.hierarchy

    setHoveredRegionIndex(regionIndex)
    setHoveredLocationIndex(locationIndex)
    setHoveredBuildingIndex(buildingIndex)

    if (locationSearchTerm.trim()) setLocationSearchRegionIndex(regionIndex)
    if (buildingSearchTerm.trim()) setBuildingSearchHierarchy({ regionIndex, locationIndex })
  }

  const handleLocationChange = (option: ILocationHierarchyOption) => {
    const { regionIndex, locationIndex, buildingIndex } = option.hierarchy

    setHoveredRegionIndex(regionIndex)
    setHoveredLocationIndex(locationIndex)
    setHoveredBuildingIndex(buildingIndex)

    if (buildingSearchTerm.trim()) setBuildingSearchHierarchy({ regionIndex, locationIndex })
  }

  const handleBuildingChange = (option: ILocationHierarchyOption) => {
    const { regionIndex, locationIndex, buildingIndex } = option.hierarchy

    setHoveredRegionIndex(regionIndex)
    setHoveredLocationIndex(locationIndex)
    setHoveredBuildingIndex(buildingIndex)
  }

  const handleSubmit = (option: ILocationHierarchyOption) => {
    reportEvent('location_selector_used')

    const { regionIndex, locationIndex, buildingIndex } = option.hierarchy
    const targetLocations = locCtx.regions[regionIndex]?.locations
    const hoveredLocation = targetLocations?.[locationIndex]
    const isAllLocationSelected = hoveredLocation ? locCtx.isAllLocation(hoveredLocation) : false

    // If location has only one building, automatically select it
    const locationHasOnlyOneBuilding = hoveredLocation?.buildings.length === 2
    const newBuildingIndex = !isAllLocationSelected && locationHasOnlyOneBuilding ? 1 : buildingIndex

    // if region has only one location, automatically select it
    const regionHasOnlyOneLocation = targetLocations?.length === 2
    const newLocationIndex = !isAllLocationSelected && regionHasOnlyOneLocation ? 1 : locationIndex

    locCtx.setSelectedRegionIndex(regionIndex)
    locCtx.setSelectedLocationIndex(newLocationIndex)
    locCtx.setSelectedBuildingIndex(newBuildingIndex)

    if (locationSearchTerm.trim()) setLocationSearchRegionIndex(regionIndex)
    if (buildingSearchTerm.trim()) setBuildingSearchHierarchy({ regionIndex, locationIndex: newLocationIndex })
  }

  const handleRegionSearch = (searchTerm: string) => {
    setRegionSearchTerm(searchTerm)
  }

  const handleLocationSearch = (searchTerm: string) => {
    const wasSearchActive = locationSearchTerm.trim().length > 0
    const isSearchActive = searchTerm.trim().length > 0

    if (!wasSearchActive && isSearchActive) setLocationSearchRegionIndex(hoveredRegionIndex)
    if (!isSearchActive) setLocationSearchRegionIndex(undefined)
    setLocationSearchTerm(searchTerm)
  }

  const handleBuildingSearch = (searchTerm: string) => {
    const wasSearchActive = buildingSearchTerm.trim().length > 0
    const isSearchActive = searchTerm.trim().length > 0

    if (!wasSearchActive && isSearchActive) {
      setBuildingSearchHierarchy({ regionIndex: hoveredRegionIndex, locationIndex: hoveredLocationIndex })
    }
    if (!isSearchActive) setBuildingSearchHierarchy(undefined)
    setBuildingSearchTerm(searchTerm)
  }

  return {
    disableSelect,
    showLocationList,
    showBuildingList,
    hoveredRegionIndex,
    hoveredLocationIndex,
    hoveredBuildingIndex,
    selectedRegionIndex: locCtx.selectedRegionIndex,
    selectedLocationIndex: locCtx.selectedLocationIndex,
    selectedBuildingIndex: locCtx.selectedBuildingIndex,
    menuButtonLabels,
    activeSearchBoxId,
    regionOptions,
    locationOptions,
    buildingOptions,
    locationSearchTerm,
    buildingSearchTerm,
    regionSearchTerm,
    setActiveSearchBoxId,
    handleRegionChange,
    handleLocationChange,
    handleBuildingChange,
    handleRegionSearch,
    handleLocationSearch,
    handleBuildingSearch,
    handleSubmit,
  }
}
